import type { FC } from 'react';
import { Fragment, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import {
  BiData,
  BiContextProvidedValue,
  ItemClickBiEvent,
  DefaultBiEvent,
  OpenStudioPresetsBiEvent,
} from '@wix/da-bi/pkg/events';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { gallectionTypeToResourceType } from '@wix/da-shared-react/pkg/types/gallection';
import { PapiUser } from '@wix/da-papi-types';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { Url } from '@wix/da-url';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { Nav } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import StudioDraftIcon from '@wix/da-ds/pkg/Icons/16x16/StudioDraft';
import StudioPublishedIcon from '@wix/da-ds/pkg/Icons/16x16/StudioPublished';
import StudioStashIcon from '@wix/da-ds/pkg/Icons/16x16/StudioStash';
import StudioPlaygroundIcon from '@wix/da-ds/pkg/Icons/24x24/StudioPlayground';
import StudioGalleryIcon from '@wix/da-ds/pkg/Icons/16x16/StudioGallery';
import StudioDiscountIcon from '@wix/da-ds/pkg/Icons/16x16/StudioDiscount';
import StudioProductsIcon from '@wix/da-ds/pkg/Icons/16x16/StudioProducts';
import StudioReportsIcon from '@wix/da-ds/pkg/Icons/16x16/StudioAnalytics';
import EarningsIcon from '@wix/da-ds/pkg/Icons/16x16/Earnings';
import ApplicationsIcon from '@wix/da-ds/pkg/Icons/16x16/Applications';
import FolderIcon from '@wix/da-ds/pkg/Icons/16x16/Folder';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import UserAvatar from '@wix/da-shared-react/pkg/User/Avatar/UserAvatar';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { usePushModal } from '@wix/da-shared-react/pkg/utils/hooks/usePushModal';
import { StudioSection } from '../../types/store';
import StorageCapacityWithUpsell from '../StorageCapacityWithUpsell';
import StudioNavigationListItem, {
  type StudioNavigationListItemProps,
} from './StudioNavigationListItem';
import useBookmarksStream from '../../hooks/useBookmarksStream';
import BookmarkActionsDropdown from '../BookmarkActionsDropdown';
import {
  getActiveSection,
  getIsDiscountsSection,
  getIsDraftsSection,
  getIsProductsSection,
} from '../../selectors/studio';
import { getHasApps } from '../../selectors/studio/apps';

import s from './StudioNavigationDesktop.scss';

export interface Props {
  className?: string;
}

const StudioNavigationDesktop: FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const user = useContext(SessionContext).user as PapiUser;

  const sectionLinks = useSectionLinks();
  const { items: bookmarks = [] } = useBookmarksStream();
  const showPresetsModal = usePushModal(ModalType.STUDIO_PRESETS);

  return (
    <Nav className={cn(s['root'], className)}>
      <div className={cn(s['identity'])}>
        <BiLoggerContextProvider value={{ sectionname: 'identity_navigation' }}>
          <UserAvatar user={user} size={40} />
          <Heading>
            <PlainButtonOrLink
              href={Url.studio({ section: 'home' })}
              biData={BiData<DefaultBiEvent>({
                evid: 2,
                click_info: 'my_studio_header',
                sectionname: BiContextProvidedValue,
              })}
            >
              {t('studio.sidebar.myStudio')}
            </PlainButtonOrLink>

            <PlainButtonOrLink
              onClick={showPresetsModal}
              className={s['settings']}
              biData={BiData<OpenStudioPresetsBiEvent>({
                evid: 19,
                sectionname: BiContextProvidedValue,
              })}
            >
              {t('studio.header.settings')}
            </PlainButtonOrLink>
          </Heading>
        </BiLoggerContextProvider>
      </div>

      <div className={cn(s['section-links'], s['sidebar-scroll-wrapper'])}>
        {sectionLinks.map(({ key, label, message, items }) => (
          <Fragment key={key}>
            <Heading className={s['section-group-label']}>{label}</Heading>
            {message && <div className={s['message']}>{message}</div>}

            {items.map(({ key: itemKey, ...sectionLinkProps }) => (
              <StudioNavigationListItem
                key={itemKey}
                // todo: any
                {...(sectionLinkProps as any)}
              />
            ))}
          </Fragment>
        ))}

        <BiLoggerContextProvider value={{ sectionname: 'bookmarks_menu' }}>
          <Heading className={s['section-group-label']}>
            {t('studio.navigation.group.bookmarks')}
          </Heading>
          {bookmarks.length === 0 && (
            <div className={s['message']}>
              {t('studio.navigation.group.bookmarks.empty')}
            </div>
          )}
          <div>
            {bookmarks.length > 0 &&
              bookmarks.map(gallection => (
                <StudioNavigationListItem
                  className={s['bookmarks-item']}
                  key={`${gallection.type}-${gallection.folderId}`}
                  label={`${gallection.name}`}
                  href={`${Url.studioFolder(gallection.folderId, gallection.type)}`}
                  prefixIcon={
                    gallection.type === 'stash' ? FolderIcon : StudioGalleryIcon
                  }
                  isLabelBold={false}
                  suffix={
                    <BookmarkActionsDropdown
                      className={s['bookmarks-menu']}
                      folder={gallection}
                    />
                  }
                  biData={BiData<ItemClickBiEvent>({
                    evid: 100,
                    view: BiContextProvidedValue,
                    component: BiContextProvidedValue,
                    page_typeid: BiContextProvidedValue,
                    page_itemid: BiContextProvidedValue,
                    sectionname: BiContextProvidedValue,
                    typeid: gallectionTypeToResourceType(gallection.type),
                    itemid: gallection.folderId,
                  })}
                />
              ))}
          </div>
        </BiLoggerContextProvider>
      </div>
      <StorageCapacityWithUpsell className={s['storage-capacity']} />
    </Nav>
  );
};
StudioNavigationDesktop.displayName = 'StudioNavigationDesktop';

export default StudioNavigationDesktop;

export type SectionLinkItem = StudioNavigationListItemProps & { key: string };

export type SectionLink = {
  key: string;
  label: string;
  message?: string;
  items: SectionLinkItem[];
};

function useSectionLinks(): SectionLink[] {
  const { t } = useTranslation();
  const activeSection = useSelector(getActiveSection);
  const isProductsSection = useSelector(getIsProductsSection);
  const isDiscountsSection = useSelector(getIsDiscountsSection);
  const isDraftsSection = useSelector(getIsDraftsSection);
  const hasApps = useSelector(getHasApps);

  return [
    {
      key: 'group-first',
      items: [
        {
          key: StudioSection.DRAFTS,
          label: t('studio.navigation.tabs.drafts'),
          href: Url.studio(),
          prefixIcon: StudioDraftIcon,
          isLabelBold: isDraftsSection,
        },
        {
          key: StudioSection.PUBLISHED,
          label: t('studio.navigation.tabs.published'),
          href: Url.studio({ section: 'published' }),
          prefixIcon: StudioPublishedIcon,
          isLabelBold: [
            StudioSection.PUBLISHED,
            StudioSection.PUBLISHED_GALLERIES,
          ].includes(activeSection),
        },
        {
          key: StudioSection.PLAYGROUND,
          label: t('studio.navigation.tabs.playground'),
          href: Url.studio({ playground: 'dreamup' }),
          prefixIcon: StudioPlaygroundIcon,
          isLabelBold: activeSection === StudioSection.PLAYGROUND,
        },
        {
          key: StudioSection.STASH,
          label: t('studio.navigation.tabs.stash'),
          href: Url.studioStash(),
          prefixIcon: StudioStashIcon,
          isLabelBold: activeSection === StudioSection.STASH,
        },
        hasApps && {
          key: StudioSection.APPS,
          label: t('studio.navigation.tabs.apps'),
          href: Url.studio({ section: 'apps' }),
          prefixIcon: ApplicationsIcon,
          isLabelBold: [
            StudioSection.APPS,
            StudioSection.APPS_UNLISTED,
          ].includes(activeSection),
        },
      ].filter(item => !!item) as SectionLinkItem[],
    },
    {
      key: 'group-third',
      label: t('studio.navigation.group.shop'),
      items: [
        {
          key: StudioSection.PRODUCTS_LOBBY,
          label: t('studio.navigation.tabs.products_lobby'),
          href: Url.studio({ products: 'root' }),
          prefixIcon: StudioProductsIcon,
          isLabelBold: isProductsSection,
        },
        {
          key: StudioSection.SHOP_REPORTS,
          label: t('studio.navigation.tabs.shop_reports'),
          href: Url.studio({ section: 'shop-reports' }),
          prefixIcon: StudioReportsIcon,
          isLabelBold: activeSection === StudioSection.SHOP_REPORTS,
        },
        {
          key: StudioSection.SELLER_DISCOUNTS_ACTIVE,
          label: t('studio.navigation.tabs.discounts'),
          href: Url.studio({ section: 'discounts', discounts: 'active' }),
          prefixIcon: StudioDiscountIcon,
          isLabelBold: isDiscountsSection,
        },
        {
          key: StudioSection.EARNINGS,
          label: t('studio.navigation.tabs.earnings'),
          href: Url.studio({ section: 'earnings' }),
          prefixIcon: EarningsIcon,
          isLabelBold: activeSection === StudioSection.EARNINGS,
        },
      ].filter(Boolean),
    },
  ].filter(Boolean) as SectionLink[];
}
